import { useTransactionTimer } from "@bkry/bowline-components/CustomHook";
import { TrunkatedAddress } from "@bkry/bowline-components/Utils";
import { chains as useChains } from "@bkry/bowline-connectors";
import {
  genericAbiRead,
  getContractAttributes,
} from "@bkry/bowline-redux/contracts";
import { getTokenAttributes } from "@bkry/bowline-redux/tokens";
import { useWeb3React } from "@web3-react/core";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import ScrollAnimation from "react-animate-on-scroll";
import { FormProvider, useForm } from "react-hook-form";
import { FaCaretDown, FaCaretUp } from "react-icons/fa";
import { MdCheckCircle } from "react-icons/md";
import ReactPlayer from "react-player";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Col,
  Collapse,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { attachedTokenForAbi, claimersAbi } from "../lootboxAbiMethods";
import ChipSelection from "./utils/ChipSelection";
import ClaimForm from "./utils/ClaimForm";
// import UserLootboxTimer from "./utils/UserLootboxTimer";
import { prepareAttachChip } from "./utils/attachHelpers";
import {
  getFormNotFilledStatus,
  getPrize,
  prepareWalletTokenClaim,
  preparePrizeFormSubmit,
} from "./utils/claimHelpers";
import { prepareDetachChip } from "./utils/detachHelpers";
import AttachMedium from "./utils/AttachMedium";

// const REACT_APP_GENERATOR_MINIO_ENDPOINT = "https://metadata.bowline.app/0xsamsunggen3rator/assets"; // ! TODO

const { REACT_APP_GENERATOR_CLAIM_PHASE, REACT_APP_GENERATOR_MINIO_ENDPOINT } =
  typeof window !== "undefined" && window.env ? window.env : process.env;

const UserLootbox = (
  {
    lootBoxId,
    chipIds,
    claimingEnabled,
    attachingEnabled,
    chipsContractAddress,
    generatorContractAddress,
    setHideGenericModals,
    useMetadata,
    getTokens,
    minAttachTime,
  },
  context
) => {
  const dispatch = useDispatch();
  const token = useSelector((state) => getTokenAttributes(state, lootBoxId));
  const contract = useSelector((state) =>
    getContractAttributes(state, `${token.contract_address},${token.network}`)
  );
  const { CHAINS } = useChains;

  const networkObject = Object.values(CHAINS).find(
    (network) =>
      network?.apiName?.toLowerCase() === contract?.network?.toLowerCase() ||
      network?.name?.toLowerCase() === contract?.network?.toLowerCase() ||
      network?.name?.toLowerCase().startsWith(contract?.network?.toLowerCase())
  );

  const partnerName = token?.metadata
    ? JSON.parse(token.metadata)?.attributes?.find(
        (att) => att.trait_type === "Partner"
      ).value || "Samsung"
    : "N/A";
  const partnerType = partnerName?.toLocaleLowerCase(); // ! TODO check if this is actually right

  const [attached, setAttached] = useState(null);
  const [attachedAt, setAttachedAt] = useState(null);
  const [attachedIdentifier, setAttachedIdentifier] = useState("");
  const [attachVideoStep, setAttachVideoStep] = useState("initial");
  const [claimed, setClaimed] = useState(false);
  const [formNotSubmitted, setFormNotSubmitted] = useState(null);
  const claimable = attached && !claimed && claimingEnabled;
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [showDetachModal, setShowDetachModal] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [showPrizeModal, setShowPrizeModal] = useState(false);
  const [currentAttachStep, setCurrentAttachStep] = useState(1);
  const [currentClaimStep, setCurrentClaimStep] = useState(1);
  const [currentPrize, setCurrentPrize] = useState(null);
  const [showPrize, setShowPrize] = useState(false);
  const [attachError, setAttachError] = useState(null);
  const [detachError, setDetachError] = useState(null);
  const [selectedChip, setSelectedChip] = useState({
    chipId: null,
    tokenIdentifier: null,
  });
  const [detaching, setDetaching] = useState(false);
  const [claimError, setClaimError] = useState(null);
  const [showCollapseError, setShowCollapseError] = useState(false);
  if (minAttachTime && attachedAt && minAttachTime < 0) {
    // just to get rid of warnings
    console.log(minAttachTime, attachedAt);
  }
  const attaching = currentAttachStep === 2;
  const claiming = currentClaimStep === 2;
  const transactionPending = attaching || claiming;
  const [transactionTimeInSeconds, resetTransactionTimer] =
    useTransactionTimer(transactionPending);
  const MAX_SECONDS_UNTIL_CLOSE_MESSAGE = 30;

  useEffect(() => {
    if (!transactionPending && transactionTimeInSeconds) {
      resetTransactionTimer();
    }
  }, [transactionTimeInSeconds, currentAttachStep, currentClaimStep]);

  const { account, provider, connector, chainId } = useWeb3React();

  const handleOpenAttach = () => {
    setShowAttachModal(true);
    setHideGenericModals(true);
  };

  const handleCloseAttach = () => {
    if (attachError) setAttachError(null);
    if (selectedChip) setSelectedChip(null);

    getTokens();
    setShowAttachModal(false);
    setCurrentAttachStep(1);
    setHideGenericModals(false);
    getAttachedTokenData();
  };

  // const handleOpenDetach = () => {
  //   setShowDetachModal(true);
  //   setHideGenericModals(true);
  // };

  const handleCloseDetach = () => {
    if (detachError) setDetachError(null);

    getTokens();
    setShowDetachModal(false);
    setHideGenericModals(false);
    getAttachedTokenData();
  };

  const handleOpenClaim = (step = 1) => {
    if (currentClaimStep !== step) setCurrentClaimStep(step);
    setShowClaimModal(true);
    setHideGenericModals(true);
  };

  const handleCloseClaim = () => {
    getTokens();
    setShowClaimModal(false);
    setClaimError(null);
    clearErrors();
    setCurrentClaimStep(1);
    setHideGenericModals(false);
    getClaimStatus();
  };

  const handleTogglePrize = () => {
    setShowPrizeModal(!showPrizeModal);
  };

  const handleAttachChip = async () => {
    await prepareAttachChip({
      generatorContractAddress,
      generatorTokenId: token?.identifier,
      generatorContractName: "Gen3rator",
      generatorContractChainId: parseInt(networkObject?.chainHex),
      chipsContractAddress,
      chipTokenId: selectedChip?.tokenIdentifier,
      wallet: account,
      provider,
      connector,
      successCallbackFnc: () => {
        setCurrentAttachStep(2);
        setAttachVideoStep("attaching");
      },
      errorCallbackFnc: (e) => {
        setAttachError(e);
        setShowAttachModal(true);
        setAttachVideoStep("initial");
      },
      transactionCallbackFnc: () => {
        getAttachedTokenData();
        if (!attaching) setCurrentAttachStep(2);
        setShowAttachModal(true);
      },
    });
  };

  const handleDetachChip = async () => {
    await prepareDetachChip({
      generatorContractAddress,
      generatorTokenId: token?.identifier,
      generatorContractName: "Gen3rator",
      generatorContractChainId: parseInt(networkObject?.chainHex),
      wallet: account,
      provider,
      connector,
      successCallbackFnc: () => setDetaching(true),
      errorCallbackFnc: (e) => {
        setDetachError(e);
        setDetaching(false);
      },
      transactionCallbackFnc: () => {
        setDetaching(false);
        handleCloseDetach();
      },
    });
  };

  const handleClaim = async () => {
    await prepareWalletTokenClaim({
      generatorContractAddress,
      generatorTokenId: token?.identifier,
      generatorContractName: "GEN3RATOR",
      generatorContractChainId: parseInt(networkObject?.chainHex),
      claimPhase: REACT_APP_GENERATOR_CLAIM_PHASE,
      provider,
      connector,
      minter: account,
      successCallbackFnc: setCurrentClaimStep(2),
      errorCallbackFnc: (data) => {
        setClaimError(data);
        setShowClaimModal(true);
      },
      transactionCallbackFnc: async () => {
        getClaimStatus();
        setCurrentClaimStep(2);
        setShowClaimModal(true);
      },
    });
  };

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
  } = useForm();
  const methods = { control, handleSubmit, errors };

  const handleFormSubmit = async (fields = {}) => {
    await preparePrizeFormSubmit({
      provider,
      connector,
      account,
      contractAddress: generatorContractAddress,
      tokenId: token?.identifier,
      chainId,
      claimPhase: REACT_APP_GENERATOR_CLAIM_PHASE,
      prize: currentPrize?.prize,
      errorCallbackFnc: (e) => {
        setError("root", {
          type: "response",
          message: e?.message,
        });
      },
      transactionCallbackFnc: () => {
        handleCloseClaim();
        handleTogglePrize();
      },
      ...fields,
    });
  };

  const renderSteps = (currentStep, totalSteps) => {
    const steps = [];

    // Loop through totalSteps and generate elements
    for (let step = 1; step <= totalSteps; step++) {
      steps.push(
        <div
          key={step}
          className={`rounded rounded-circle border border-white d-flex align-items-center justify-content-center pt-1 ${
            currentStep === step ? "bg-white text-dark" : ""
          }`}
          style={{ width: "2rem", height: "2rem" }}
        >
          {step}
        </div>
      );
    }
    return (
      <div className="my-4 d-flex justify-content-center">
        <div
          className="d-flex align-items-center justify-content-around flex-grow-1"
          style={{ maxWidth: "360px" }}
        >
          {steps}
        </div>
      </div>
    );
  };

  const renderMedia = () => {
    return (
      <>
        {animationUrl ? (
          <ReactPlayer
            width="100%"
            height="auto"
            url={animationUrl}
            controls={false}
            loop={true}
            muted={true}
            playing={true}
            playsinline
            config={{
              file: {
                attributes: {
                  controlsList: "nodownload noplaybackrate",
                  disablePictureInPicture: true,
                },
              },
            }}
          />
        ) : (
          <>
            {imageUrl && (
              <img
                src={imageUrl}
                alt={token.name}
                className="object-fit-cover"
              />
            )}
          </>
        )}
      </>
    );
  };

  // const renderMergeMedia = () => {
  //   // const path = `${REACT_APP_GENERATOR_MINIO_ENDPOINT}/${partnerType}/attaching.mp4`;
  //   const path = "/videos/ledger.mp4";
  //   return (
  //     <>
  //       <ReactPlayer
  //         width="100%"
  //         height="auto"
  //         url={path}
  //         controls={false}
  //         loop={false}
  //         muted={true}
  //         playing={true}
  //         playsinline
  //         config={{
  //           file: {
  //             attributes: {
  //               controlsList: "nodownload noplaybackrate",
  //               disablePictureInPicture: true,
  //             },
  //           },
  //         }}
  //       />
  //     </>
  //   );
  // };

  const renderActiveMedia = () => {
    const path = `${REACT_APP_GENERATOR_MINIO_ENDPOINT}/${partnerType}/active.mp4`;
    return (
      <>
        <div className="ratio ratio-1x1 overflow-hidden">
          <ReactPlayer
            width="100%"
            height="auto"
            url={path}
            controls={false}
            loop={true}
            muted={true}
            playing={true}
            playsinline
            config={{
              file: {
                attributes: {
                  controlsList: "nodownload noplaybackrate",
                  disablePictureInPicture: true,
                },
              },
            }}
          />
        </div>
      </>
    );
  };

  const getAttachedTokenData = async () => {
    if (useMetadata) return false;
    if (!attachedTokenForAbi?.name) return false;
    dispatch(
      genericAbiRead({
        functionAbi: attachedTokenForAbi,
        contractAddress: token.contract_address,
        account: account,
        provider: provider,
        genericInputs: {
          tokenIdentifier: token.identifier,
        },
        successCallbackFnc: (data) => {
          console.log("attached token data", data);
          if (Object.hasOwn(data, "attached")) {
            setAttached(data.attached);
          }
          if (Object.hasOwn(data, "attachedAt")) {
            const time = parseInt(data.attachedAt) * 1000;
            setAttachedAt(new Date(time));
          }
          if (Object.hasOwn(data, "tokenIdentifier")) {
            setAttachedIdentifier(data.tokenIdentifier.toString());
          }
        },
        errorCallbackFnc: (data) => {
          console.log("attached token data error", data);
        },
      })
    );
  };

  const getClaimStatus = async () => {
    if (useMetadata) return false;
    if (!claimersAbi?.name) return false;
    dispatch(
      genericAbiRead({
        functionAbi: claimersAbi,
        contractAddress: token.contract_address,
        account: account,
        provider: provider,
        mappingInputs: [REACT_APP_GENERATOR_CLAIM_PHASE, token.identifier],
        successCallbackFnc: (data) => {
          console.log("claim status", data);
          if (!!data && data !== "0x0000000000000000000000000000000000000000") {
            setClaimed(true);
          } else {
            setClaimed(false);
          }
        },
        errorCallbackFnc: (data) => {
          console.log("claim status error", data);
        },
      })
    );

    const response = await getFormNotFilledStatus({
      contractAddress: generatorContractAddress,
      tokenId: token.identifier,
    });

    setFormNotSubmitted(response);
  };

  useEffect(() => {
    if (!useMetadata) {
      getAttachedTokenData();
      getClaimStatus();
    }
  }, [useMetadata]);

  useEffect(async () => {
    if (
      (currentClaimStep === 3 || showPrizeModal || showPrize) &&
      !currentPrize
    ) {
      const prize = await getPrize({
        chainId: chainId,
        contractAddress: generatorContractAddress,
        tokenId: token.identifier,
        claimPhase: REACT_APP_GENERATOR_CLAIM_PHASE,
      });

      setCurrentPrize(prize);
    }
  }, [currentClaimStep, showPrizeModal, showPrize]);

  const getTokenAnimation = () => {
    let animationPath = null;
    if (claimed && attached) {
      animationPath = `${REACT_APP_GENERATOR_MINIO_ENDPOINT}/${partnerType}/used.mp4`;
    } else if (attached) {
      animationPath = `${REACT_APP_GENERATOR_MINIO_ENDPOINT}/${partnerType}/active.mp4`;
    }
    return animationPath;
  };
  const getTokenImage = () => {
    let imagePath = null;
    if (!attached) {
      imagePath = `${REACT_APP_GENERATOR_MINIO_ENDPOINT}/${partnerType}/inactive.png`;
    }
    return imagePath;
  };

  const chainIdToNetwork = {
    1: "ethereum",
    3: "ropsten",
    4: "rinkeby",
    5: "goerli",
    42: "kovan",
    11155111: "sepolia",
    137: "polygon",
    80001: "mumbai",
    80002: "amoy",
    100: "lukso",
    1007: "lukso_testnet",
  };

  const correctNetwork = token.network === chainIdToNetwork[chainId] || false;

  const animationUrl = correctNetwork
    ? getTokenAnimation()
    : JSON.parse(token.metadata)?.animation_url;
  const imageUrl = correctNetwork
    ? getTokenImage()
    : JSON.parse(token.metadata)?.image;

  return (
    <>
      <Row className="h-100">
        <Col xs={6}>
          <div className="ratio ratio-1x1 rounded-4 overflow-hidden">
            {renderMedia()}
          </div>
        </Col>
        <Col xs={6} className="d-flex flex-column">
          <p className="fw-bold mb-0">
            {token.name || `${context.t("GEN3RATOR")} #${token?.identifier}`}
          </p>
          <p className="small text-muted">
            <TrunkatedAddress address={token.contract_address} /> (
            {contract?.network})
          </p>
          <p className="small">
            {context.t("Partner")}
            <br />
            <b>{partnerName}</b>
          </p>
          {attached && (
            <p className="small">
              {context.t("Attached")}
              <br />
              <b>
                {context.t("GEN3 Chip")} #{attachedIdentifier}
              </b>
            </p>
          )}

          {attached ? (
            <div className="mt-auto">
              {claimable && (
                <Button
                  color="primary"
                  block
                  onClick={() => handleOpenClaim(1)}
                >
                  {context.t("Claim")}
                </Button>
              )}
              {claimed && !formNotSubmitted && (
                <div className="text-center small ">
                  <Button color="primary" block onClick={handleTogglePrize}>
                    {context.t("View Prize")}
                  </Button>
                </div>
              )}
              {claimed && formNotSubmitted && (
                <Button
                  color="primary"
                  block
                  onClick={() => handleOpenClaim(3)}
                >
                  {context.t("Continue Claim")}
                </Button>
              )}
              {!claimed && !claimable && (
                <div className="text-center small ">
                  {context.t("Currently nothing to claim")}
                </div>
              )}
              {/* <UserLootboxTimer
                handleOpenDetach={handleOpenDetach}
                attachedAt={attachedAt}
                minAttachTime={minAttachTime}
              /> */}
            </div>
          ) : attachingEnabled ? (
            <Button
              color="primary"
              className="mt-auto"
              onClick={handleOpenAttach}
              disabled={!chipIds?.length}
            >
              {context.t("Attach GEN3 Chip")}
            </Button>
          ) : useMetadata ? (
            <div className="text-center small ">
              {context.t("Connect to {networkName} network first", {
                networkName: token?.network,
              })}
            </div>
          ) : (
            <div className="text-center small ">
              {context.t("Attaching is disabled")}
            </div>
          )}
        </Col>
      </Row>
      <Modal
        isOpen={showDetachModal}
        size="md"
        toggle={handleCloseDetach}
        centered
      >
        <ModalHeader toggle={handleCloseDetach}>
          {context.t("Detach GEN3 Chip")}
          {detaching && <Spinner className="mx-2" size="sm" color="primary" />}
        </ModalHeader>
        <ModalBody className="p-4 d-flex flex-column">
          <div className="d-flex flex-column justify-content-center my-auto">
            <p className="h4">
              {context.t("Do you really want to detach your {attachedChip}?", {
                attachedChip: attached?.name || `${context.t("GEN3 Chip")}`,
              })}
            </p>
            <ul>
              {!claimed && (
                <li>
                  {context.t(
                    "You can't claim rewards with the GEN3RATOR until you attach again a GEN3 Chip"
                  )}
                </li>
              )}
              <li>{context.t("GEN3 Chip will go back to your Inventory")}</li>
              <li>
                {context.t("You can use the GEN3 Chip again on any GEN3RATOR")}
              </li>
            </ul>

            {detachError && (
              <p className="text-danger">{detachError?.message}</p>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            outline
            onClick={handleCloseDetach}
            disabled={detaching}
          >
            {context.t("Cancel")}
          </Button>
          {/* TODO trigger detaching */}
          <Button
            color="primary"
            onClick={handleDetachChip}
            disabled={detaching}
          >
            {context.t("Confirm")}
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        isOpen={showClaimModal}
        size="xl"
        toggle={handleCloseClaim}
        fullscreen={true}
        id="scrollable_modal"
      >
        <ModalBody className="p-4 d-flex flex-column">
          <button
            className="btn-close-white btn-close position-absolute top-0 end-0 p-4"
            onClick={handleCloseClaim}
            aria-label={context.t("close")}
          />
          {renderSteps(currentClaimStep, 3)}
          <div className="d-flex flex-column justify-content-center align-items-center my-auto">
            {currentClaimStep === 1 && (
              <Container className="mt-5">
                <Row>
                  <Col md={5} className="pb-3">
                    <div className="ratio ratio-1x1 rounded-4 overflow-hidden">
                      {renderMedia()}
                    </div>
                    <p className="fw-bold text-center mt-3 mb-0">
                      {token.name ||
                        `${context.t("GEN3RATOR")} #${token?.identifier}`}
                      {partnerName && ` (${partnerName})`}
                    </p>
                  </Col>
                  <Col className="d-flex flex-column pb-3">
                    <p className="h1 mb-5">
                      {context.t(
                        "Do you reallly want to claim your {tokenName} reward?",
                        { tokenName: token?.name || "GEN3RATOR" }
                      )}
                    </p>
                    <ul>
                      <li>
                        {context.t(
                          "Once claimed the GEN3RATOR can not claim this reward again"
                        )}
                      </li>
                      <li>
                        {context.t(
                          "Depending on the reward you need to enter different contact information"
                        )}
                      </li>
                    </ul>
                    <Button
                      color="primary"
                      className="mt-auto"
                      onClick={handleClaim}
                    >
                      {context.t("Claim")}
                    </Button>
                  </Col>
                </Row>
              </Container>
            )}
            {claiming && (
              <Container className="mt-5">
                {claimError ? (
                  <div className="d-flex flex-column align-items-center">
                    <h1 className="h1 text-center">
                      {context.t("Something went wrong")}
                    </h1>
                    <p className="text-center">
                      {context.t(
                        "Something unexpected happened, please restart the service and view the error."
                      )}
                    </p>

                    <span
                      className="text-danger small cursor-pointer text-center"
                      onClick={() => setShowCollapseError(!showCollapseError)}
                      style={{ marginBottom: "1rem" }}
                    >
                      {context.t("show full error message")}{" "}
                      {!showCollapseError && <FaCaretDown />}{" "}
                      {showCollapseError && <FaCaretUp />}
                    </span>
                    <Collapse isOpen={showCollapseError}>
                      <pre className="text-wrap text-break">
                        {claimError?.message}
                      </pre>
                    </Collapse>

                    <Button
                      color="primary"
                      className="mt-3"
                      onClick={handleCloseClaim}
                    >
                      {context.t("Close")}
                    </Button>
                  </div>
                ) : (
                  <Row>
                    {!showPrize ? (
                      <>
                        <Col md={6} className="pb-3">
                          {renderActiveMedia()}
                        </Col>
                        <Col md={6} className="d-flex flex-column pb-3">
                          <div className="h-50 d-flex flex-column justify-content-center">
                            {!claimed ? (
                              <>
                                <div className="d-flex align-items-baseline gap-1">
                                  <Spinner
                                    style={{ width: "1rem", height: "1rem" }}
                                    color="primary"
                                  />

                                  <p className="fw-bold">
                                    {context.t("Claiming your Reward")}
                                  </p>
                                </div>

                                <p>
                                  {context.t(
                                    "Your reward is being claimed. Sit tight!"
                                  )}
                                </p>

                                {transactionTimeInSeconds >=
                                  MAX_SECONDS_UNTIL_CLOSE_MESSAGE && (
                                  <p className="small">
                                    {context.t(
                                      "This transaction takes longer than usual."
                                    )}
                                  </p>
                                )}
                              </>
                            ) : (
                              <>
                                <div className="d-flex align-items-baseline gap-1">
                                  <MdCheckCircle className="text-success me-2" />
                                  <p className="fw-bold">
                                    {context.t("Reward claimed!")}
                                  </p>
                                </div>

                                <p>
                                  {context.t(
                                    "Congratulations! Your reward has been successfully claimed. However, we need some information from you to complete the process. Please continue."
                                  )}
                                </p>
                              </>
                            )}
                          </div>
                          <Button
                            color="primary"
                            className="mt-auto"
                            disabled={!claimed}
                            onClick={() => setShowPrize(true)}
                          >
                            {context.t("Continue")}
                          </Button>
                        </Col>
                      </>
                    ) : currentPrize ? (
                      <>
                        <div className="my-5">
                          <div className="h1 text-center">
                            {currentPrize?.title}
                          </div>
                          <div className="h3 text-center">
                            {currentPrize?.prize}
                          </div>
                        </div>
                        <Col md={{ size: 6, offset: 3 }}>
                          <ScrollAnimation
                            animateIn={"fadeInUp"}
                            delay={400}
                            scrollableParentSelector="#scrollable_modal"
                          >
                            <div className="ratio ratio-4x3 overflow-hidden">
                              <img
                                src={currentPrize?.imageUrl}
                                alt={currentPrize?.prize}
                                style={{ objectFit: "cover" }}
                              />
                            </div>
                          </ScrollAnimation>
                        </Col>
                        <Button
                          color="primary"
                          className="mt-3"
                          disabled={!claimed}
                          onClick={() => setCurrentClaimStep(3)}
                        >
                          {context.t("Continue")}
                        </Button>
                      </>
                    ) : (
                      <div className="d-flex align-items-center justify-content-center">
                        <Spinner color="primary" />
                      </div>
                    )}
                  </Row>
                )}
              </Container>
            )}
            {currentClaimStep === 3 && (
              <Container>
                {currentPrize ? (
                  <>
                    <div className="my-5">
                      <div className="h1 text-center">
                        {currentPrize?.title}
                      </div>
                      <div className="h3 text-center">
                        {currentPrize?.prize}
                      </div>
                    </div>
                    <Row>
                      <Col md={5}>
                        <ScrollAnimation
                          animateIn={"fadeInLeft"}
                          delay={400}
                          scrollableParentSelector="#scrollable_modal"
                        >
                          <div className="ratio ratio-1x1 overflow-hidden">
                            <img
                              src={currentPrize?.imageUrl}
                              alt={currentPrize?.prize}
                              style={{ objectFit: "cover" }}
                            />
                          </div>
                        </ScrollAnimation>
                      </Col>
                      <Col className="d-flex flex-column pb-3">
                        {isSubmitting ? (
                          <>
                            <div className="d-flex align-items-baseline gap-1">
                              <Spinner
                                style={{ width: "1rem", height: "1rem" }}
                                color="primary"
                              />
                              <p className="fw-bold">
                                {context.t("Submitting Form")}
                              </p>
                            </div>

                            <p>
                              {context.t(
                                "Kindly wait while we get the last step to finish"
                              )}
                            </p>
                          </>
                        ) : (
                          <FormProvider {...methods}>
                            <ClaimForm
                              onSubmit={handleFormSubmit}
                              inputType={currentPrize?.inputType}
                            />
                          </FormProvider>
                        )}
                      </Col>
                    </Row>
                  </>
                ) : (
                  <div className="d-flex align-items-center justify-content-center">
                    <Spinner color="primary" />
                  </div>
                )}
              </Container>
            )}
          </div>
        </ModalBody>
      </Modal>
      <Modal
        isOpen={showAttachModal}
        size="xl"
        toggle={handleCloseAttach}
        fullscreen={true}
      >
        <ModalBody className="p-4 d-flex flex-column">
          <button
            className="btn-close-white btn-close position-absolute top-0 end-0 p-4"
            onClick={handleCloseAttach}
            aria-label={context.t("close")}
          />
          {renderSteps(currentAttachStep, 3)}
          <div className="d-flex flex-column justify-content-center align-items-center my-auto">
            {attachError ? (
              <div className="d-flex flex-column align-items-center">
                <h1 className="h1 text-center">
                  {context.t("Something went wrong")}
                </h1>
                <p className="text-center">
                  {context.t(
                    "Something unexpected happened, please restart the service and view the error."
                  )}
                </p>

                <span
                  className="text-danger small cursor-pointer text-center"
                  onClick={() => setShowCollapseError(!showCollapseError)}
                  style={{ marginBottom: "1rem" }}
                >
                  {context.t("show full error message")}{" "}
                  {!showCollapseError && <FaCaretDown />}{" "}
                  {showCollapseError && <FaCaretUp />}
                </span>
                <Collapse isOpen={showCollapseError}>
                  <pre className="text-wrap text-break">
                    {attachError?.message}
                  </pre>
                </Collapse>

                <Button
                  color="primary"
                  className="mt-3"
                  onClick={handleCloseAttach}
                >
                  {context.t("Close")}
                </Button>
              </div>
            ) : (
              <Container className="mt-5">
                <Row>
                  <Col md={7} className="pb-3">
                    <AttachMedium
                      partnerType={partnerType}
                      attachVideoStep={attachVideoStep}
                    />
                    <p className="fw-bold text-center mt-3 mb-0">
                      {token.name ||
                        `${context.t("GEN3RATOR")} #${token?.identifier}`}
                      {partnerName && ` (${partnerName})`}
                    </p>
                  </Col>

                  {currentAttachStep === 1 && (
                    <Col md={5} className="d-flex flex-column pb-3">
                      <p className="h1 mb-5">
                        {context.t("Attach GEN3 Chip to your GEN3RATOR")}
                      </p>
                      <p>
                        {context.t(
                          "Select one of your GEN3 Chips you want to attach"
                        )}
                      </p>
                      <Row>
                        {chipIds?.map((chipId) => (
                          <Col
                            key={chipId}
                            xs={6}
                            md={4}
                            xl={3}
                            className="mb-3 text-center"
                          >
                            <ChipSelection
                              chipId={chipId}
                              selectedChip={selectedChip}
                              setSelectedChip={setSelectedChip}
                            />
                          </Col>
                        ))}
                      </Row>
                      <Button
                        color="primary"
                        className="mt-auto"
                        disabled={selectedChip?.chipId === null}
                        onClick={handleAttachChip}
                      >
                        {context.t("Attach now")}
                      </Button>
                    </Col>
                  )}
                  {attaching && (
                    <Col md={5} className="d-flex flex-column pb-3">
                      <div className="h-100 d-flex flex-column justify-content-center">
                        {!attached ? (
                          <>
                            <div className="d-flex align-items-baseline gap-1">
                              <Spinner
                                style={{ width: "1rem", height: "1rem" }}
                                color="primary"
                              />

                              <p className="fw-bold">
                                {context.t("Attaching GEN3 Chip")}
                              </p>
                            </div>

                            <p>
                              {context.t(
                                "The chosen GEN3 Chip is being attached to your GEN3RATOR. In just a moment, your GEN3RATOR will be activated!"
                              )}
                            </p>

                            {transactionTimeInSeconds >=
                              MAX_SECONDS_UNTIL_CLOSE_MESSAGE && (
                              <p className="small">
                                {context.t(
                                  "This transaction takes longer than usual."
                                )}
                              </p>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="d-flex align-items-baseline gap-1">
                              <MdCheckCircle className="text-success me-2" />
                              <p className="fw-bold">
                                {context.t("GEN3 Chip Attached!")}
                              </p>
                            </div>

                            <p>
                              {context.t(
                                "GEN3 Chip was successfully attached to your GEN3RATOR"
                              )}
                            </p>
                          </>
                        )}
                      </div>
                      <Button
                        color="primary"
                        className="mt-auto"
                        disabled={!attached}
                        onClick={() => {
                          setAttachVideoStep("rotating");
                          setCurrentAttachStep(3);
                        }}
                      >
                        {context.t("Continue")}
                      </Button>
                    </Col>
                  )}
                  {currentAttachStep === 3 && (
                    <Col md={5} className="d-flex flex-column pb-3">
                      <div className="my-5 h1">
                        {context.t("Attaching successful")}
                      </div>
                      <p>
                        {context.t(
                          "Congratulations! Your GEN3 Chip is now attached to your GEN3RATOR"
                        )}
                      </p>
                      <ul>
                        <li>
                          {context.t(
                            "Once claim window is open use your activated GEN3RATOR to claim your rewards"
                          )}
                        </li>
                        <li>
                          {context.t(
                            "Claiming is only possible if you hold the activated GEN3RATOR"
                          )}
                        </li>
                        {/* <li>
                          {context.t(
                            "You can detach your GEN3 Chip no earlier than 24 hours after attaching it"
                          )}
                        </li> */}
                      </ul>
                      <Button
                        color="primary"
                        className="mt-auto"
                        onClick={handleCloseAttach}
                      >
                        {context.t("Close")}
                      </Button>
                    </Col>
                  )}
                </Row>
              </Container>
            )}
          </div>
        </ModalBody>
      </Modal>
      <Modal
        isOpen={showPrizeModal}
        size="xl"
        toggle={handleTogglePrize}
        fullscreen={true}
      >
        <ModalBody className="p-4 d-flex flex-column">
          <button
            className="btn-close-white btn-close position-absolute top-0 end-0 p-4"
            onClick={handleTogglePrize}
            aria-label={context.t("close")}
          />
          <div className="d-flex flex-column justify-content-center align-items-center my-auto">
            <Container className="mt-5">
              {currentPrize ? (
                <Row>
                  <Col md={5}>
                    <div className="img-div position-relative">
                      <img
                        src={currentPrize?.imageUrl}
                        alt={currentPrize?.title}
                        className="img-fluid"
                      />
                    </div>
                  </Col>
                  <Col className="d-flex flex-column pb-3">
                    <p className="h1 mb-5">{currentPrize?.title}</p>
                    <p>
                      <p>{currentPrize?.prize}.</p>
                    </p>
                    <Button
                      color="primary"
                      className="mt-auto"
                      onClick={handleTogglePrize}
                    >
                      {context.t("Close")}
                    </Button>
                  </Col>
                </Row>
              ) : (
                <div className="d-flex align-items-center justify-content-center">
                  <Spinner color="primary" />
                </div>
              )}
            </Container>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

UserLootbox.contextTypes = {
  t: PropTypes.func,
};

UserLootbox.propTypes = {
  lootBoxId: PropTypes.string.isRequired,
  chipIds: PropTypes.array,
  claimingEnabled: PropTypes.bool.isRequired,
  attachingEnabled: PropTypes.bool.isRequired,
  chipsContractAddress: PropTypes.string.isRequired,
  generatorContractAddress: PropTypes.string.isRequired,
  setHideGenericModals: PropTypes.func.isRequired,
  useMetadata: PropTypes.bool,
  getTokens: PropTypes.func.isRequired,
  minAttachTime: PropTypes.number.isRequired,
};

UserLootbox.defaultProps = {
  useMetadata: false,
};

export default UserLootbox;
